import _ from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import { VersionHeader } from "src/ui/shared/table/headers/VersionHeader";

import { GroupFilterHeader } from "@dashboard/shared/components/GroupFilterHeader/GroupFilterHeader";
import { getBadgeContent } from "@dashboard/shared/Filtering.utils";
import { FilterHeader } from "@shared/table/headers/FilterHeader";
import { MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { Header, TableHeaderRows } from "@shared/table/TableHeader";
import { ColumnFiltersState, Header as TanstackHeader, Table } from "@tanstack/react-table";

import {
    DeviceFilterType,
    getInitialCodenameFilters,
    getInitialLicenseStatusFilters,
    getInitialOnlineStatusFilters,
    getInitialSystemVersionFilters,
    getInitialWarningsFilters,
} from "./filters/DeviceTableFilters.utils";
import { DeviceTableData } from "./DeviceTable.utils";
import { SelectAllLicensesCheckbox } from "./SelectAllLicensesCheckbox";
import { useColumnDrag } from "./useColumnDrag";

interface DeviceTableFilterProps {
    table: Table<DeviceTableData>;
    columnFilters: ColumnFiltersState;
    setFilters: (id: DeviceFilterType, values: Record<string, boolean>) => void;
}

type DeviceTableHeaderCellProps = DeviceTableFilterProps & {
    header: TanstackHeader<DeviceTableData, unknown>;
    systemVersions: string[];
};

const DeviceTableHeaderCell = ({ header, table, columnFilters, setFilters, systemVersions }: DeviceTableHeaderCellProps) => {
    const { t } = useTranslation(["pages", "common"]);
    const columnDrag = useColumnDrag(table, header.id);

    if (header.id === "moveLicenseCheckbox") {
        return <SelectAllLicensesCheckbox header={header} id={header.id} table={table} />;
    }
    if (header.id === "groupName") {
        return (
            <GroupFilterHeader
                header={header}
                columnDrag={columnDrag}
                table={table}
                badgeContent={getBadgeContent("groupName", columnFilters)}
                onSubmit={(values) => setFilters("groupName", values)}
            />
        );
    }
    if (header.id === "licenseStatus") {
        return (
            <Header {...header} columnDrag={columnDrag}>
                <FilterHeader
                    title={t("pages:devicePage.table.filter.licenseStatus")}
                    sortDisabled
                    options={[
                        { key: "Used", label: t("common:status.used") },
                        { key: "Unused", label: t("common:status.unused") },
                    ]}
                    initialValues={getInitialLicenseStatusFilters(table)}
                    onSubmit={(values) => setFilters("licenseStatus", values)}
                />
            </Header>
        );
    }
    if (header.id === "onlineStatus") {
        return (
            <Header {...header} columnDrag={columnDrag}>
                <FilterHeader
                    title={t("pages:devicePage.table.filter.status")}
                    sortDisabled
                    options={[
                        { key: "Online", label: t("pages:devicePage.status.deviceOnline") },
                        { key: "Offline", label: t("pages:devicePage.status.deviceOffline") },
                    ]}
                    initialValues={getInitialOnlineStatusFilters(table)}
                    onSubmit={(values) => setFilters("onlineStatus", values)}
                />
            </Header>
        );
    }
    if (header.id === "warnings") {
        return (
            <Header {...header} columnDrag={columnDrag}>
                <FilterHeader
                    title={t("pages:devicePage.table.header.health")}
                    sortDisabled
                    options={[
                        { key: "OK", label: t("pages:devicePage.table.health.ok") },
                        { key: "Warning", label: t("pages:devicePage.table.health.warning") },
                    ]}
                    initialValues={getInitialWarningsFilters(table)}
                    onSubmit={(values) => setFilters("warnings", values)}
                />
            </Header>
        );
    }
    if (header.id === "systemVersion") {
        return (
            <Header {...header} columnDrag={columnDrag}>
                <VersionHeader
                    title={t("pages:devicePage.table.filter.systemVersion")}
                    options={systemVersions.map((version) => ({
                        key: version,
                        label: version,
                    }))}
                    initialValues={getInitialSystemVersionFilters(table)}
                    onSubmit={(values) => setFilters("systemVersion", values)}
                />
            </Header>
        );
    }
    if (header.id === "codename") {
        const codenames = _.uniq(_.compact(table.getCoreRowModel().rows.map((row) => row.original.codename)));
        return (
            <Header {...header} columnDrag={columnDrag}>
                <FilterHeader
                    title={t("pages:devicePage.table.filter.codename")}
                    options={codenames.map((codename) => ({
                        key: codename,
                        label: codename,
                    }))}
                    initialValues={getInitialCodenameFilters(table)}
                    onSubmit={(values) => setFilters("codename", values)}
                />
            </Header>
        );
    } else {
        return <Header {...header} columnDrag={columnDrag} />;
    }
};

export const DeviceTableHeaders: React.FC<DeviceTableFilterProps> = ({ table, columnFilters, setFilters }) => {
    const systemVersions = React.useMemo(() => {
        return _.uniq(_.compact(table.getCoreRowModel().rows.map((row) => row.original.systemVersion)));
    }, [table]);

    return (
        <MinimalTableHeader>
            <TableHeaderRows
                headerGroups={table.getHeaderGroups().slice(1)}
                renderHeader={(header) => (
                    <DeviceTableHeaderCell
                        header={header}
                        table={table}
                        columnFilters={columnFilters}
                        setFilters={setFilters}
                        systemVersions={systemVersions}
                    />
                )}
            />
        </MinimalTableHeader>
    );
};
